import {
  IDefect,
  IFlatRateUnit,
  IFlatRateUnitGroup,
  IJob,
  IPackage,
  IPart,
  IPicturePanel,
  IRepairOverview,
  IServiceContract,
  ISublet,
  ITechnicalDocument
} from '@service-and-repairs/awpintegrationlib';
import {PackageType} from '@service-and-repairs/dms-api';
import {Util} from '../../../util/util';

export class JobProcessor {

  static setExternalIdsIfMissing(job: IJob): void {
    JobProcessor.setDefectsExternalIdIfMissing(job.getDefects());
    JobProcessor.setFlatRateUnitsExternalIdIfMissing(job.getFlatRateUnits());
    JobProcessor.setPartsExternalIdIfMissing(job.getParts());
    JobProcessor.setSubletsExternalIdIfMissing(job.getSublets());
    JobProcessor.setPackagesExternalIdIfMissing(job.getPackages());
    JobProcessor.setFlatRateUnitGroupsExternalIdIfMissing(job.getFlatRateUnitGroups());
    JobProcessor.setPicturePanelsExternalIdIfMissing(job.getPicturePanels());
    JobProcessor.setRepairOverviewsExternalIdIfMissing(job.getRepairOverviews());
    JobProcessor.setTechnicalDocumentsExternalIdIfMissing(job.getTechnicalDocuments());
  }

  private static setDefectsExternalIdIfMissing(defects: IDefect[]): void {
    defects.forEach((defect: IDefect): void => {
      if (!defect.getExternalId()) {
        defect.setExternalId(Util.createRandomUuid());
      }
    });
  }

  private static setFlatRateUnitsExternalIdIfMissing(flatRateUnits: IFlatRateUnit[]): void {
    flatRateUnits.forEach((flatRateUnit: IFlatRateUnit): void => {
      if (!flatRateUnit.getExternalId()) {
        flatRateUnit.setExternalId(Util.createRandomUuid());
      }
    });
  }

  private static setPartsExternalIdIfMissing(parts: IPart[]): void {
    parts.forEach((part: IPart): void => {
      if (!part.getExternalId()) {
        part.setExternalId(Util.createRandomUuid());
      }
    });
  }

  private static setSubletsExternalIdIfMissing(sublets: ISublet[]): void {
    sublets.forEach((sublet: ISublet): void => {
      if (!sublet.getExternalId()) {
        sublet.setExternalId(Util.createRandomUuid());
      }
    });
  }

  private static setPackagesExternalIdIfMissing(packs: IPackage[]): void {
    packs.forEach((pack: IPackage): void => {
      if (!pack.getExternalId()) {
        pack.setExternalId(Util.createRandomUuid());
      }
      JobProcessor.setDefectsExternalIdIfMissing(pack.getDefects());
      JobProcessor.setFlatRateUnitsExternalIdIfMissing(pack.getFlatRateUnits());
      JobProcessor.setPartsExternalIdIfMissing(pack.getParts());
      JobProcessor.setSubletsExternalIdIfMissing(pack.getSublets());
    });
  }

  private static setFlatRateUnitGroupsExternalIdIfMissing(flatRateUnitGroups: IFlatRateUnitGroup[]): void {
    flatRateUnitGroups.forEach((flatRateUnitGroup: IFlatRateUnitGroup): void => {
      if (!flatRateUnitGroup.getExternalId()) {
        flatRateUnitGroup.setExternalId(Util.createRandomUuid());
      }
      JobProcessor.setFlatRateUnitsExternalIdIfMissing(flatRateUnitGroup.getFlatRateUnits());
    });
  }

  private static setPicturePanelsExternalIdIfMissing(picturePanels: IPicturePanel[]): void {
    picturePanels.forEach((picturePanel: IPicturePanel): void => {
      if (!picturePanel.getExternalId()) {
        picturePanel.setExternalId(Util.createRandomUuid());
      }
      JobProcessor.setPartsExternalIdIfMissing(picturePanel.getParts());
    });
  }

  private static setRepairOverviewsExternalIdIfMissing(repairOverviews: IRepairOverview[]): void {
    repairOverviews.forEach((repairOverview: IRepairOverview): void => {
      if (!repairOverview.getExternalId()) {
        repairOverview.setExternalId(Util.createRandomUuid());
      }
      JobProcessor.setFlatRateUnitGroupsExternalIdIfMissing(repairOverview.getFlatRateUnitGroups());
      JobProcessor.setPicturePanelsExternalIdIfMissing(repairOverview.getPicturePanels());
      if (repairOverview.getDefect()) {
        JobProcessor.setDefectsExternalIdIfMissing([repairOverview.getDefect()]);
      }
    });
  }

  private static setTechnicalDocumentsExternalIdIfMissing(technicalDocuments: ITechnicalDocument[]): void {
    technicalDocuments.forEach((technicalDocument: ITechnicalDocument): void => {
      if (!technicalDocument.getExternalId()) {
        technicalDocument.setExternalId(Util.createRandomUuid());
      }
    });
  }

  static hasPackage(job: IJob, servicePackage: IPackage): boolean {
    return servicePackage && job.getPackages().some((pack: IPackage): boolean =>
      pack.getNumber() === servicePackage.getNumber() && pack.getType() === servicePackage.getType()
    );
  }

  static hasServiceContract(job: IJob, serviceContract: IServiceContract): boolean {
    return job.getServiceContract()?.getOptionCode() === serviceContract.getOptionCode();
  }

  static addParts(job: IJob, partsToAdd: IPart[]): void {
    const parts: IPart[] = job.getParts();
    partsToAdd.forEach((partToAdd: IPart): void => {
      partToAdd.setIsSelected(true);
      const index: number =
        parts.findIndex((part: IPart): boolean => part.getPartNumber() === partToAdd.getPartNumber());
      if (index >= 0) {
        parts[index].setQuantity(parts[index].getQuantity() + partToAdd.getQuantity());
      } else {
        parts.push(partToAdd);
      }
    });
    JobProcessor.setPartsExternalIdIfMissing(parts);
    job.setIsSelected(true);
    JobProcessor.setPartPositionNumbers(job.getParts());
  }

  static addFlatRateUnits(job: IJob, flatRateUnitsToAdd: IFlatRateUnit[]): void {
    const flatRateUnits: IFlatRateUnit[] = job.getFlatRateUnits();
    flatRateUnitsToAdd.forEach((fruToAdd: IFlatRateUnit): void => {
      fruToAdd.setIsSelected(true);
      flatRateUnits.push(fruToAdd);
    });
    JobProcessor.setFlatRateUnitsExternalIdIfMissing(flatRateUnits);
    job.setIsSelected(true);
    JobProcessor.setFlatRateUnitPositionNumbers(job.getFlatRateUnits());
  }

  static addOrReplacePackage(job: IJob, packageToAdd: IPackage): void {
    JobProcessor.preparePackage(job, packageToAdd);
    const packages: IPackage[] = job.getPackages();
    const index: number = packages.findIndex((): boolean => JobProcessor.hasPackage(job, packageToAdd));
    if (index >= 0) {
      packages[index] = packageToAdd;
    } else {
      packages.push(packageToAdd);
    }
    job.setIsSelected(true);
    JobProcessor.setPackagePositionNumbers(job.getPackages());
  }

  static addOrReplaceServiceContract(job: IJob, serviceContractToAdd: IServiceContract): void {
    if (!serviceContractToAdd.getExternalId()) {
      serviceContractToAdd.setExternalId(Util.createRandomUuid());
    }
    serviceContractToAdd.setIsSelected(true);
    job.setServiceContract(serviceContractToAdd);
    job.setIsSelected(true);
  }

  static addDefects(job: IJob, defectsToAdd: IDefect[]): void {
    const defects: IDefect[] = job.getDefects();
    defectsToAdd.forEach((defectToAdd: IDefect): void => {
      defectToAdd.setIsSelected(true);
      const index: number = defects.findIndex((defect: IDefect): boolean => defect.getCode() === defectToAdd.getCode());
      if (index >= 0) {
        defects[index] = defectToAdd;
      } else {
        defects.push(defectToAdd);
      }
    });
    JobProcessor.setDefectsExternalIdIfMissing(defects);
    job.setIsSelected(true);
    JobProcessor.setDefectPositionNumbers(job.getDefects());
  }

  static addFlatRateUnitGroup(job: IJob, flatRateUnitGroupToAdd: IFlatRateUnitGroup): void {
    const flatRateUnitGroups: IFlatRateUnitGroup[] = job.getFlatRateUnitGroups();
    const index: number = flatRateUnitGroups.findIndex((flatRateUnitGroup: IFlatRateUnitGroup): boolean =>
      flatRateUnitGroup.getSupplierDocumentId() === flatRateUnitGroupToAdd.getSupplierDocumentId());
    flatRateUnitGroupToAdd.setAddedAt(new Date());
    flatRateUnitGroupToAdd.getFlatRateUnits().forEach((fru: IFlatRateUnit): void => fru.setIsSelected(true));
    if (index >= 0) {
      flatRateUnitGroups[index] = flatRateUnitGroupToAdd;
    } else {
      flatRateUnitGroups.push(flatRateUnitGroupToAdd);
    }
    JobProcessor.setFlatRateUnitGroupsExternalIdIfMissing(flatRateUnitGroups);
    job.setIsSelected(true);
    JobProcessor.setFlatRateUnitGroupPositionNumbers(job.getFlatRateUnitGroups());
  }

  static addPicturePanel(job: IJob, picturePanelToAdd: IPicturePanel): void {
    const picturePanels: IPicturePanel[] = job.getPicturePanels();
    const index: number = picturePanels.findIndex((picturePanel: IPicturePanel): boolean =>
      picturePanel.getSupplierDocumentId() === picturePanelToAdd.getSupplierDocumentId());
    picturePanelToAdd.setAddedAt(new Date());
    picturePanelToAdd.getParts().forEach((part: IPart): void => part.setIsSelected(true));
    if (index >= 0) {
      picturePanels[index] = picturePanelToAdd;
    } else {
      picturePanels.push(picturePanelToAdd);
    }
    JobProcessor.setPicturePanelsExternalIdIfMissing(picturePanels);
    job.setIsSelected(true);
    JobProcessor.setPicturePanelPositionNumbers(job.getPicturePanels());
  }

  static addRepairOverview(job: IJob, repairOverviewToAdd: IRepairOverview): void {
    const repairOverviews: IRepairOverview[] = job.getRepairOverviews();
    const index: number = repairOverviews.findIndex((repairOverview: IRepairOverview): boolean =>
      repairOverview.getSupplierDocumentId() === repairOverviewToAdd.getSupplierDocumentId());
    repairOverviewToAdd.setAddedAt(new Date());
    repairOverviewToAdd.getFlatRateUnitGroups()
      .forEach((group: IFlatRateUnitGroup): void => group.getFlatRateUnits()
        .forEach((fru: IFlatRateUnit): void => fru.setIsSelected(true)));
    repairOverviewToAdd.getPicturePanels()
      .forEach((panel: IPicturePanel): void => panel.getParts()
        .forEach((part: IPart): void => part.setIsSelected(true)));
    repairOverviewToAdd.getDefect()?.setIsSelected(true);
    if (index >= 0) {
      repairOverviews[index] = repairOverviewToAdd;
    } else {
      repairOverviews.push(repairOverviewToAdd);
    }
    JobProcessor.setRepairOverviewsExternalIdIfMissing(repairOverviews);
    job.setIsSelected(true);
    JobProcessor.setRepairOverviewPositionNumbers(job.getRepairOverviews());
  }

  static addTechnicalDocument(job: IJob, technicalDocumentToAdd: ITechnicalDocument): void {
    const technicalDocuments: ITechnicalDocument[] = job.getTechnicalDocuments();
    const index: number = technicalDocuments.findIndex((technicalDocument: ITechnicalDocument): boolean =>
      technicalDocument.getPublicationDocumentId() === technicalDocumentToAdd.getPublicationDocumentId());
    technicalDocumentToAdd.setAddedAt(new Date());
    if (index >= 0) {
      technicalDocuments[index] = technicalDocumentToAdd;
    } else {
      technicalDocuments.push(technicalDocumentToAdd);
    }
    JobProcessor.setTechnicalDocumentsExternalIdIfMissing(technicalDocuments);
    job.setIsSelected(true);
    JobProcessor.setTechnicalDocumentPositionNumbers(job.getTechnicalDocuments());
  }

  static isEmpty(job: IJob): boolean {
    return job.getParts().length === 0
      && job.getFlatRateUnits().length === 0
      && job.getDefects().length === 0
      && job.getSublets().length === 0
      && job.getPackages().length === 0
      && job.getFlatRateUnitGroups().length === 0
      && job.getPicturePanels().length === 0
      && job.getRepairOverviews().length === 0
      && job.getTechnicalDocuments().length === 0
      && (job.getServiceContract() === null || job.getServiceContract() === undefined);
  }

  private static setSelectedOnAllItems(job: IJob, isSelected: boolean): void {
    job.setIsSelected(isSelected);

    job.getParts().forEach((part: IPart): void => part.setIsSelected(isSelected));
    job.getFlatRateUnits().forEach((flatRateUnit: IFlatRateUnit): void => flatRateUnit.setIsSelected(isSelected));
    job.getDefects().forEach((defect: IDefect): void => defect.setIsSelected(isSelected));
    job.getSublets().forEach((sublet: ISublet): void => sublet.setIsSelected(isSelected));

    job.getServiceContract()?.setIsSelected(isSelected);

    job.getPackages().forEach((pack: IPackage): void => {
      pack.setIsSelected(isSelected);

      pack.getParts().forEach((part: IPart): void => part.setIsSelected(isSelected));
      pack.getFlatRateUnits().forEach((flatRateUnit: IFlatRateUnit): void => flatRateUnit.setIsSelected(isSelected));
      pack.getDefects().forEach((defect: IDefect): void => defect.setIsSelected(isSelected));
      pack.getSublets().forEach((sublet: ISublet): void => sublet.setIsSelected(isSelected));
    });
  }

  // TODO: Sort before setting position numbers
  private static setPartPositionNumbers(parts: IPart[]): void {
    for (let i: number = 0; i < parts.length; i++) {
      parts[i].setPositionNumber(i + 1);
    }
  }

  // TODO: Sort before setting position numbers
  private static setFlatRateUnitPositionNumbers(flatRateUnits: IFlatRateUnit[]): void {
    for (let i: number = 0; i < flatRateUnits.length; i++) {
      flatRateUnits[i].setPositionNumber(i + 1);
    }
  }

  // TODO: Sort before setting position numbers
  private static setPackagePositionNumbers(packs: IPackage[]): void {
    for (let i: number = 0; i < packs.length; i++) {
      packs[i].setPositionNumber(i + 1);
    }
  }

  // TODO: Sort before setting position numbers
  private static setDefectPositionNumbers(defects: IDefect[]): void {
    for (let i: number = 0; i < defects.length; i++) {
      defects[i].setPositionNumber(i + 1);
    }
  }

  // TODO: Sort before setting position numbers
  private static setFlatRateUnitGroupPositionNumbers(flatRateUnitGroups: IFlatRateUnitGroup[]): void {
    for (let i: number = 0; i < flatRateUnitGroups.length; i++) {
      flatRateUnitGroups[i].setPositionNumber(i + 1);
    }
  }

  // TODO: Sort before setting position numbers
  private static setPicturePanelPositionNumbers(picturePanels: IPicturePanel[]): void {
    for (let i: number = 0; i < picturePanels.length; i++) {
      picturePanels[i].setPositionNumber(i + 1);
    }
  }

  // TODO: Sort before setting position numbers
  private static setRepairOverviewPositionNumbers(repairOverviews: IRepairOverview[]): void {
    for (let i: number = 0; i < repairOverviews.length; i++) {
      repairOverviews[i].setPositionNumber(i + 1);
    }
  }

  // TODO: Sort before setting position numbers
  private static setTechnicalDocumentPositionNumbers(technicalDocuments: ITechnicalDocument[]): void {
    for (let i: number = 0; i < technicalDocuments.length; i++) {
      technicalDocuments[i].setPositionNumber(i + 1);
    }
  }

  private static setSubletPositionNumbers(sublets: ISublet[]): void {
    for (let i: number = 0; i < sublets.length; i++) {
      sublets[i].setPositionNumber(i + 1);
    }
  }

  static prepareJob(job: IJob): void {
    job.setDemandCategory(job.getDemandCategory() || 'AWP');
    JobProcessor.setExternalIdsIfMissing(job);
    JobProcessor.setPartPositionNumbers(job.getParts());
    JobProcessor.setFlatRateUnitPositionNumbers(job.getFlatRateUnits());
    JobProcessor.setDefectPositionNumbers(job.getDefects());
    JobProcessor.setSubletPositionNumbers(job.getSublets());
    JobProcessor.setPackagePositionNumbers(job.getPackages());
    JobProcessor.setFlatRateUnitGroupPositionNumbers(job.getFlatRateUnitGroups());
    JobProcessor.setPicturePanelPositionNumbers(job.getPicturePanels());
    JobProcessor.setRepairOverviewPositionNumbers(job.getRepairOverviews());
    JobProcessor.setTechnicalDocumentPositionNumbers(job.getTechnicalDocuments());
    job.getPackages().forEach((pack: IPackage): void => JobProcessor.preparePackage(job, pack));
    JobProcessor.setSelectedOnAllItems(job, true);
  }

  private static preparePackage(job: IJob, pack: IPackage): void {
    pack.setIsSelected(true);

    if ([PackageType.Sip.toString(), PackageType.Grp.toString()].includes(pack.getType())) {
      pack.setGrossPrice(0);
      pack.getParts().forEach((part: IPart): void => part.setTotalCustomerGrossPrice(0));
      pack.getFlatRateUnits().forEach((flatRateUnit: IFlatRateUnit): void => flatRateUnit.setGrossPrice(0));
    }

    JobProcessor.setDefectPositionNumbers(pack.getDefects());
    JobProcessor.setFlatRateUnitPositionNumbers(pack.getFlatRateUnits());
    JobProcessor.setPartPositionNumbers(pack.getParts());
    JobProcessor.setSubletPositionNumbers(pack.getSublets());

    JobProcessor.setPackagesExternalIdIfMissing([pack]);
    job.getPackages().forEach((pack: IPackage): void => {
      JobProcessor.setDefectsExternalIdIfMissing(pack.getDefects());
      JobProcessor.setFlatRateUnitsExternalIdIfMissing(pack.getFlatRateUnits());
      JobProcessor.setPartsExternalIdIfMissing(pack.getParts());
      JobProcessor.setSubletsExternalIdIfMissing(pack.getSublets());
    });
  }
}
